import { ArrowRight, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Hero = () => {
  const { t } = useLang();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-center overflow-hidden pt-24"
      style={{
        background:
          "radial-gradient(ellipse at 20% 10%, rgba(0,255,102,0.10) 0%, rgba(0,255,102,0) 45%), radial-gradient(ellipse at 85% 50%, rgba(0,255,102,0.06) 0%, rgba(0,255,102,0) 40%), #0b0c10",
      }}
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/2971104/pexels-photo-2971104.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b0c10]/70 via-[#0b0c10]/85 to-[#0b0c10]" />
      <div className="grain" />

      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,255,102,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,102,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="fade-in-up delay-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(0,255,102,0.3)] bg-[rgba(0,255,102,0.06)] mb-8">
            <span className="w-2 h-2 rounded-full bg-[#00FF66] shadow-[0_0_10px_#00FF66]" />
            <span className="text-xs font-heading tracking-widest text-[#9CA3AF] uppercase">
              {t("hero_badge")}
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="fade-in-up delay-2 font-heading text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-extrabold"
          >
            {t("hero_title_1")} {t("hero_title_2")} {t("hero_title_3")}
            <br />
            <span className="font-normal text-white/85 text-3xl sm:text-4xl lg:text-5xl block mt-4">
              {t("hero_welcome")}{" "}
              <span className="text-[#00FF66]" style={{ textShadow: "0 0 20px rgba(0,255,102,0.5)" }}>
                Loka Beds
              </span>
              .
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="fade-in-up delay-3 mt-8 max-w-2xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed"
          >
            {t("hero_sub_1")} <span className="text-white">{t("hero_sub_2")}</span> {t("hero_sub_3")}{" "}
            <span className="text-[#00FF66] font-semibold">{t("hero_sub_4")}</span>{" "}
            {t("hero_sub_5")}
          </p>

          <div className="fade-in-up delay-4 mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollTo("rooms")}
              className="btn-neon"
            >
              {t("hero_cta")} <ArrowRight size={16} />
            </button>
            <button
              data-testid="hero-about-btn"
              onClick={() => scrollTo("about")}
              className="btn-ghost"
            >
              {t("hero_about_btn")}
            </button>
          </div>

          <div className="fade-in-up delay-4 mt-12 flex flex-wrap gap-8 text-sm text-[#9CA3AF]">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-[#00FF66]" />
              <span>{t("hero_stat_location")}</span>
            </div>
            <div>
              <span className="text-white font-semibold">13</span> {t("hero_stat_rooms")}
            </div>
            <div>
              <span className="text-white font-semibold">24/7</span> {t("hero_stat_reception")}
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-5 relative fade-in-up delay-3">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[rgba(0,255,102,0.25)]">
            <img
              src="https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/uyhu6wtg_WhatsApp%20Image%202026-07-05%20at%203.07.01%20PM%20%282%29.jpeg"
              alt="Loka Beds room"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c10] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <div className="text-xs text-[#9CA3AF] font-heading tracking-widest uppercase">
                  {t("hero_featured")}
                </div>
                <div className="text-white font-heading text-2xl font-bold">
                  {t("room_single_title")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#00FF66] font-heading text-2xl font-bold">
                  IDR 148K
                </div>
                <div className="text-xs text-[#9CA3AF]">{t("hero_night")}</div>
              </div>
            </div>
          </div>
          <div
            className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(0,255,102,0.35) 0%, rgba(0,255,102,0) 70%)",
              filter: "blur(8px)",
            }}
          />
        </div>
      </div>

      <div className="wave-container" data-testid="hero-wave">
        <div className="wave-layer wave-1" />
        <div className="wave-layer wave-2" />
        <div className="wave-layer wave-3" />
      </div>
    </section>
  );
};

export default Hero;
